import { useMemo } from 'react';
import { NoDataComponent } from '../../../../components/formsAndTable/forms/formControls/NoDataComponent';
import AjaxSourcedData from '../../../../components/formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { ScoringUserFilter } from '../../../../components/scorings/components/ScoringUserFilter';
import { TableColumn } from 'react-data-table-component';
import { ScoreBoardData } from '../../../../types/Scoring';
import { createLessonColumns } from '../../../../components/scorings/components/LessonsColumn';
import { getUserColumn } from '../../../../components/scorings/components/UsersColumn';
import { useTranslation } from 'react-i18next';
import { baseLang } from '../../../../lib/utils';

type ScoreScrollTableProps = {
  rows: any[]; // Replace with actual data type
  //   columns: any[]; // Replace with actual column type
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  search?: string;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  handleSort?: (column: any, direction: 'asc' | 'desc') => void;
  handleSearch?: (searchText: string) => void;
  isScoreBoardLoading: boolean;
  isScoreBoardFetching: boolean;
  scoreBoard: ScoreBoardData;
  preselectedUser?: { label: string; value: string } | null;
  hasSelector?: boolean;
  userFilter?: string;
  setUserFilter?: (filter: string) => void;
  handleClearPreselectedUser?: () => void;
  hasFilter?: boolean;
  showUsernames?: boolean;
};

export const ScoreScrollTable = ({
  page,
  perPage,
  setPage,
  setPerPage,
  search,
  sortKey,
  sortDir,
  handleSort,
  handleSearch,
  isScoreBoardLoading,
  isScoreBoardFetching,
  scoreBoard,
  hasSelector = false,
  userFilter,
  setUserFilter,
  preselectedUser,
  handleClearPreselectedUser,
  hasFilter = true,
  showUsernames = true,
}: ScoreScrollTableProps) => {
  const { t, i18n } = useTranslation();
  //   const [preselectedUser, setPreselectedUser] = useState<{ label: string; value: string } | null>(
  //     null,
  //   );
  const pageLang = baseLang(i18n.language);
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const userColumn = useMemo(() => getUserColumn(t), [t]);

  const columnsSignature = scoreBoard?.columns
    ? scoreBoard.columns.map((c) => String(c.lessonId ?? '')).join(',')
    : '';

  const lessonColumnsMemo = useMemo(() => {
    if (!scoreBoard?.columns) return [] as TableColumn<ScoreBoardData['rows'][0]>[];
    return createLessonColumns(pageLang, t, viewerTz, scoreBoard.columns);
  }, [columnsSignature, pageLang, t, viewerTz]);
  const columns = useMemo(() => {
    return showUsernames ? [userColumn, ...lessonColumnsMemo] : lessonColumnsMemo;
  }, [userColumn, lessonColumnsMemo, showUsernames]);
  return (
    <AjaxSourcedData
      data={scoreBoard?.rows || []} // Pass data from RTK Query
      columns={columns} // Pass dynamically created columns
      pagination={true}
      paginationServer={true}
      paginationPerPage={perPage}
      paginationDefaultPage={page}
      paginationTotalRows={scoreBoard?.pagination?.total || 0}
      onChangePage={(newPage) => setPage(newPage)}
      onChangeRowsPerPage={(newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage || 1);
      }}
      sortServer={true}
      onSort={handleSort}
      defaultSortFieldId={sortKey}
      defaultSortAsc={sortDir === 'asc'}
      searchServer={true}
      searchText={search}
      onSearch={handleSearch}
      noDataComponent={<NoDataComponent />}
      className={`avg-table ${showUsernames ? 'with-usernames' : 'without-usernames'}`}
      customStyles={{
        head: {
          style: {
            zIndex: 32,
          },
        },
        headCells: {
          style: {
            position: showUsernames ? 'sticky' : 'static',
            top: 0,
            left: 0,
            // backgroundColor: 'white',
            zIndex: 20,
            fontWeight: 'bold',
            borderBottom: '2px solid #dee2e6',
          },
        },
        //   headRow: {
        //     style: {
        //       position: 'sticky',
        //       top: 0,
        //       zIndex: 33,
        //     },
        //   },
      }}
      fixedHeader
      fixedHeaderScrollHeight="600px"
      loading={isScoreBoardLoading || isScoreBoardFetching}
      hasFilter={hasFilter}
      selectorComponent={
        hasSelector && (
          <ScoringUserFilter
            usersList={scoreBoard?.users || []}
            setSearch={setUserFilter}
            userFilter={userFilter}
            defaultValue={preselectedUser}
            onClear={handleClearPreselectedUser}
          />
        )
      }
    />
  );
};
